import {Card} from 'platform/components';
import {Grid, ValueByDevice} from 'platform/foundation';

import {defaultTo, isEmpty, isNotEmpty, isNotNil} from 'ramda';

import {useGetWorkshopOverviewQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {useDashboardWidgetSettings} from '../../hooks/useDashboardWidgetSettings';
import {DashboardTileSize} from '../../types/DashboardTileSize';
import {WORKSHOP_OVERVIEW_DENSITY_BY_SIZE} from './constants/workshopOverviewDensityBySize';
import {WORKSHOP_OVERVIEW_TILES} from './constants/workshopOverviewTiles';
import {useWorkshopTileRedirect} from './hooks/useWorkshopTileRedirect';
import {WorkshopOverviewInteraction} from './types/WorkshopOverviewInteraction';
import {
  WorkshopOverviewSectionState,
  WorkshopOverviewTileState,
  WorkshopSection,
} from './types/WorkshopOverviewTileConfig';
import {mapWorkshopOverviewCounts} from './utils/mapWorkshopOverviewCounts';
import {WorkshopOverviewCompact} from './WorkshopOverviewCompact';
import {WorkshopOverviewSection} from './WorkshopOverviewSection';

const defaultToFalse = defaultTo(false);
const SECTION_ORDER: WorkshopSection[] = ['critical', 'warning', 'today'];

interface WorkshopOverviewProps extends TestIdProps {
  size: DashboardTileSize;
}

const getSectionColumns = (sectionCount: number): ValueByDevice<number> => [
  1,
  1,
  sectionCount,
  sectionCount,
];

export function WorkshopOverview(props: WorkshopOverviewProps) {
  const settings = useDashboardWidgetSettings();

  const enabledTiles = WORKSHOP_OVERVIEW_TILES.filter((tile) =>
    defaultToFalse(settings?.[tile.settingsKey])
  );

  const overview = useGetWorkshopOverviewQuery(undefined, {skip: enabledTiles.length === 0});
  const {prefetchTileQuery, openTileGrid, pendingTileId} = useWorkshopTileRedirect();

  const counts = mapWorkshopOverviewCounts(overview.data);

  const tileStates: WorkshopOverviewTileState[] = enabledTiles
    .map((tile) => ({tile, count: counts[tile.id]}))
    .filter((tileState) => !overview.isSuccess || isNotNil(tileState.count));

  const sections: WorkshopOverviewSectionState[] = SECTION_ORDER.map((section) => ({
    section,
    tiles: tileStates.filter((tileState) => tileState.tile.section === section),
  })).filter((sectionState) => isNotEmpty(sectionState.tiles));

  if (overview.isError || isEmpty(sections)) {
    return null;
  }

  const interaction: WorkshopOverviewInteraction = {
    isLoading: !overview.isSuccess,
    pendingTileId,
    onOpenTile: openTileGrid,
    onPrefetchTile: prefetchTileQuery,
  };

  return (
    <Card
      hasSeparator={false}
      title={i18n.t('page.homepage.workshopOverview.title')}
      isFullHeight
      data-testid={suffixTestId('workshopOverview', props)}
    >
      {WORKSHOP_OVERVIEW_DENSITY_BY_SIZE[props.size] === 'compact' ? (
        <WorkshopOverviewCompact
          sections={sections}
          interaction={interaction}
          data-testid={props['data-testid']}
        />
      ) : (
        <Grid columns={getSectionColumns(sections.length)} spacing={4}>
          {sections.map((sectionState) => (
            <WorkshopOverviewSection
              key={sectionState.section}
              section={sectionState.section}
              tiles={sectionState.tiles}
              interaction={interaction}
              data-testid={suffixTestId(`workshopOverview-${sectionState.section}`, props)}
            />
          ))}
        </Grid>
      )}
    </Card>
  );
}
